import React, { useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, Graticule, Sphere } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import styles from "../PartnerAnalyticsDashboard.module.css";

// Use a known-good TopoJSON source compatible with react-simple-maps/d3-geo.
// (Some GeoJSON hosts can fail due to CORS/availability, resulting in an empty map.)
const GEO_URL = "https://raw.githubusercontent.com/xoolive/topojson/master/world-countries.json";

const GeoDistribution = ({ items }) => {
  const [hovered, setHovered] = useState(null);

  const safeItems = items || [];
  const maxCount = useMemo(
    () => Math.max(1, ...safeItems.map((d) => Number(d.count) || 0)),
    [safeItems]
  );

  const colorScale = useMemo(
    () =>
      scaleLinear()
        .domain([0, maxCount])
        .range(["#cbd5e1", "#1e3a8a"]),
    [maxCount]
  );

  return (
    <div className={`${styles.chartFrame} ${styles.chartFrameTall} ${styles.geoFrame}`} style={{ position: "relative" }}>
      {hovered ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            zIndex: 3,
            background: "#ffffff",
            border: "1px solid rgba(15, 23, 42, 0.08)",
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12)",
            borderRadius: 10,
            padding: "10px 12px",
            minWidth: 200,
          }}
        >
          <div style={{ fontWeight: 900, color: "#0f172a", marginBottom: 4 }}>
            {hovered.country} — {hovered.percentage}%
          </div>
          <div style={{ color: "#64748b", fontWeight: 600 }}>
            {Number(hovered.count).toLocaleString("fr-FR")} événements
          </div>
        </div>
      ) : null}

      <div className={styles.geoMapWrap}>
        <ComposableMap
          className={styles.geoMap}
          projection="geoEqualEarth"
          projectionConfig={{ rotate: [-10, 0, 0], scale: 147 }}
          width={800}
          height={400}
        >
          <Sphere stroke="#f1f5f9" strokeWidth={0.6} />
          <Graticule stroke="#f1f5f9" strokeWidth={0.6} />
          <Geographies geography={GEO_URL}>
            {({ geographies }) => {
              if (!geographies?.length) {
                return (
                  <text x={16} y={28} fill="#94a3b8" fontSize={12} fontWeight={700}>
                    Carte indisponible
                  </text>
                );
              }

              return geographies.map((geo) => {
                const iso2 =
                  geo?.properties?.ISO_A2 ||
                  geo?.properties?.iso_a2 ||
                  geo?.properties?.["Alpha-2"] ||
                  geo?.properties?.ISO2 ||
                  geo?.properties?.["ISO3166-1-Alpha-2"] ||
                  geo?.id;
                const iso2Normalized = typeof iso2 === "string" ? iso2.toUpperCase() : iso2;
                const match = safeItems.find((s) => s.country === iso2Normalized);
                const fill = match ? colorScale(Number(match.count) || 0) : "#f1f5f9";
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    stroke="#ffffff"
                    strokeWidth={0.6}
                    onMouseEnter={() => {
                      if (!match) return setHovered(null);
                      setHovered(match);
                    }}
                    onMouseLeave={() => setHovered(null)}
                    style={{
                      default: { outline: "none" },
                      hover: { fill: "#3b82f6", outline: "none" },
                      pressed: { outline: "none" },
                    }}
                  />
                );
              });
            }}
          </Geographies>
        </ComposableMap>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 6 }}>
        <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 700 }}>0</span>
        <div
          aria-hidden="true"
          style={{
            width: 140,
            height: 10,
            borderRadius: 999,
            background: "linear-gradient(90deg, #cbd5e1, #1e3a8a)",
          }}
        />
        <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 700 }}>{maxCount.toLocaleString("fr-FR")}</span>
      </div>
    </div>
  );
};

export default GeoDistribution;
